import dollarsToCents from "dollars-to-cents";

/**
 * The transactions page's search. Strict rather than fuzzy: every word of the query has to appear in
 * the name (or the description), ignoring case, accents and punctuation, so "mcdonalds" finds
 * "McDonald's" but "tar" does not find half the budget. A query that reads as money also finds that
 * amount, either sign.
 *
 * Sorted by SORT_BEST_MATCH, results come in tiers and each tier is newest first, so the dates stay
 * followable within a tier: an amount match or a name that starts with the query, then names where
 * every word starts a word, then matches inside a word, then matches only in the description.
 * SORT_NEWEST is plain date order.
 *
 * The category picker still uses TransactionFinder.searchByQuery, which wants the fuzzy matches.
 */

export const SORT_BEST_MATCH = 'best';
export const SORT_NEWEST = 'newest';

const AMOUNT_QUERY = /^-?\$?\s*-?(\d{1,3}(,\d{3})+|\d+)?(\.\d{1,2})?$/;

export function normalizeSearchText(text) {
	if(!text) {
		return '';
	}
	return String(text)
		.normalize('NFKD')
		.toLowerCase()
		.replace(/[̀-ͯ]/g, '')
		// Apostrophes join rather than split ("McDonald's" is "mcdonalds"), as does the replacement
		// character some imported names have in place of one.
		.replace(/['’`�]/g, '')
		.replace(/[^a-z0-9]+/g, ' ')
		.trim();
}

/** The query as cents, when it reads as an amount ("24", "$24.50", "1,200"), otherwise null. */
export function amountFromQuery(query) {
	const trimmed = String(query).trim();
	if(!/\d/.test(trimmed) || !AMOUNT_QUERY.test(trimmed)) {
		return null;
	}
	const cents = dollarsToCents(trimmed.replace(/[$,\s-]/g, ''));
	return Number.isFinite(cents) ? cents : null;
}

/**
 * How well `normalizedName` matches the query's words, as a tier (lower is better), or null when
 * some word is missing.
 */
function nameTier(normalizedName, normalizedQuery, words) {
	if(!normalizedName) {
		return null;
	}
	if(normalizedName.startsWith(normalizedQuery)) {
		return 0;
	}
	const padded = ` ${normalizedName}`;
	let allStartWords = true;
	for(const word of words) {
		if(!normalizedName.includes(word)) {
			return null;
		}
		if(!padded.includes(` ${word}`)) {
			allStartWords = false;
		}
	}
	return allStartWords ? 1 : 2;
}

const normalizedCache = new Map();

/** normalizeSearchText, remembered: names repeat a lot, and every keystroke searches all of them. */
function normalizedName(text) {
	if(!text) {
		return '';
	}
	let normalized = normalizedCache.get(text);
	if(normalized === undefined) {
		normalized = normalizeSearchText(text);
		normalizedCache.set(text, normalized);
	}
	return normalized;
}

function timeOf(transaction) {
	return transaction.transaction_date ? transaction.transaction_date.valueOf() : -Infinity;
}

/**
 * @param {Array} transactions what to search, budget transactions or bank records
 * @param {string} query
 * @param {string} sort SORT_BEST_MATCH or SORT_NEWEST
 * @returns {Array} the matching transactions, in order
 */
export default function searchTransactions(transactions, query, sort = SORT_BEST_MATCH) {
	const normalizedQuery = normalizeSearchText(query);
	const amount = amountFromQuery(query);
	if(!normalizedQuery && amount === null) {
		return [];
	}
	const words = normalizedQuery ? normalizedQuery.split(' ') : [];

	const matches = [];
	for(const transaction of transactions) {
		let tier = null;
		if(amount !== null && Math.abs(transaction.amount) === Math.abs(amount)) {
			tier = 0;
		}
		if(tier === null && words.length) {
			tier = nameTier(normalizedName(transaction.where), normalizedQuery, words);
		}
		if(tier === null && words.length && transaction.description) {
			tier = nameTier(normalizedName(transaction.description), normalizedQuery, words) === null ? null : 3;
		}
		if(tier !== null) {
			matches.push({ transaction, tier, time: timeOf(transaction) });
		}
	}

	matches.sort((a, b) => {
		if(sort === SORT_BEST_MATCH && a.tier !== b.tier) {
			return a.tier - b.tier;
		}
		return b.time - a.time;
	});
	return matches.map((match) => match.transaction);
}
